import { withTenderTime } from "../../../context/TenderTimeProvider";
import React, { useCallback, useEffect, useRef, useState } from "react";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import style from "./OrderInfoAviation.module.scss";
import { Aviation, File } from "../../../services";
import { useLanguage } from "../../../context/LanguageContext";
import Loading from "../../shared/Loading/Loading";
import OrdersChatHub from "../../shared/chat/OrdersChatHub";
import OrderAccessRestricted from "../../orders-new/order-info/components/OrderAccessRestricted";
import OrderInfoAviationHeader from "./components/desktop/OrderInfoAviationHeader";
import OrderInfoAviationStepper from "./components/desktop/OrderInfoAviationStepper";
import OrderInfoAviationTabs from "./components/desktop/OrderInfoAviationTabs";
import AviationCargoTab from "./components/desktop/tabs/AviationCargoTab";
import AviationDocumentsTab from "./components/desktop/tabs/AviationDocumentsTab";
import AviationActionsTab from "./components/desktop/tabs/AviationActionsTab";
import { useAirOrderRealtimeEvents } from "../../../hooks/useAirOrderRealtimeEvents";
import {
  AIR_ORDER_DETAIL_REFRESH_TARGETS,
  CHAT_REFRESH_TARGETS,
} from "../../../services/orderRealtimeEvents";
import { useChatUnreadRefresh } from "../../../hooks/useChatUnreadRefresh";

const normalizeOrderDates = (sourceOrder) => {
  const order = { ...sourceOrder };

  if (order.shippingDate) {
    const [date] = order.shippingDate.split("T");
    const timeStart = String(order.shippingHourFrom ?? 0).padStart(2, "0");
    const timeEnd = String(order.shippingHourTo ?? 0).padStart(2, "0");
    order.shippingDate = `${date}T${timeStart}:00:00`;
    order.shippingDateEnd = `${date}T${timeEnd}:00:00`;
  }

  if (order.unloadingDate) {
    const [date] = order.unloadingDate.split("T");
    const timeStart = String(order.unloadingHourFrom ?? 0).padStart(2, "0");
    const timeEnd = String(order.unloadingHourTo ?? 0).padStart(2, "0");
    order.unloadingDate = `${date}T${timeStart}:00:00`;
    order.unloadingDateEnd = `${date}T${timeEnd}:00:00`;
  }

  return order;
};

const OrderInfoAviation = ({ isOpen, setIsOpen, orderId, onForceUpdate, tenderOrderInfo }) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const { user } = useSelector((state) => state);
  const requestIdRef = useRef(0);
  const documentsRequestIdRef = useRef(0);
  const actionsRequestIdRef = useRef(0);
  const initialExpeditorRef = useRef(null);
  const translationRef = useRef(t);
  translationRef.current = t;

  const [order, setOrder] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [statusHistory, setStatusHistory] = useState([]);
  const [suggestedPrices, setSuggestedPrices] = useState([]);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [activeTab, setActiveTab] = useState("actions");
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenChat, setIsOpenChat] = useState(false);
  const {
    refreshUnreadMessages,
    refreshUnreadOnClose,
    scheduleUnreadRefresh,
  } = useChatUnreadRefresh({
    getUnreadMessages: Aviation.getUnreadMessages,
    orderId,
    unreadMessagesCount,
    setUnreadMessagesCount,
  });

  const fetchDocuments = useCallback(
    async (currentOrderId) => {
      if (!currentOrderId) return;

      const requestId = ++documentsRequestIdRef.current;

      try {
        const [orderFiles, uploadedFiles] = await Promise.all([
          File.getFilesByOrder(currentOrderId, 6),
          File.getFilesByOrder(currentOrderId, 7),
        ]);
        if (requestId === documentsRequestIdRef.current) {
          setDocuments([...(orderFiles.data || []), ...(uploadedFiles.data || [])].reverse());
        }
      } catch (error) {
        if (requestId === documentsRequestIdRef.current) {
          console.error(error);
          toast.error(translationRef.current("orderInfo.genericError"));
        }
      }
    },
    [],
  );

  const fetchOrderData = useCallback(async ({ silent = false } = {}) => {
    if (!orderId || !isOpen) return;

    const requestId = ++requestIdRef.current;
    if (!silent) {
      setIsLoading(true);
    }

    try {
      const response = await Aviation.getAirOrdersById(orderId, currentLanguage);
      if (requestId === requestIdRef.current) {
        if (initialExpeditorRef.current?.orderId !== orderId) {
          initialExpeditorRef.current = { orderId, expeditorId: response.data?.expeditorId };
        }
        // Имя из списка заказов относится к исходной ТК и может устареть после переназначения.
        const isInitialExpeditor = initialExpeditorRef.current.expeditorId === response.data?.expeditorId;
        setOrder(
          normalizeOrderDates({
            ...response.data,
            expeditorName:
              (isInitialExpeditor && tenderOrderInfo?.expeditorName) || response.data?.expeditorName,
          }),
        );
      }
    } catch (error) {
      if (requestId === requestIdRef.current) {
        console.error(error);
        toast.error(translationRef.current("orderInfo.genericError"));
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [currentLanguage, isOpen, orderId, tenderOrderInfo?.expeditorName]);

  const fetchActionData = useCallback(async (currentOrderId) => {
    if (!currentOrderId) return;

    const requestId = ++actionsRequestIdRef.current;

    try {
      const [historyResponse, pricesResponse] = await Promise.all([
        Aviation.getHistory(currentOrderId),
        Aviation.getListSuggestedPriceAir(currentOrderId),
      ]);

      if (requestId !== actionsRequestIdRef.current) return;

      const history = historyResponse.data || [];
      setStatusHistory(history);
      setSuggestedPrices(pricesResponse.data || []);
    } catch (error) {
      if (requestId === actionsRequestIdRef.current) {
        console.error(error);
        toast.error(translationRef.current("orderInfo.genericError"));
      }
    }
  }, []);

  useAirOrderRealtimeEvents(({ target, data }) => {
    const isMatchingOrder =
      data?.orderId != null && String(data.orderId) === String(orderId);

    if (!isOpen || !isMatchingOrder) return;

    if (CHAT_REFRESH_TARGETS.has(target)) {
      refreshUnreadMessages();
      return;
    }

    if (AIR_ORDER_DETAIL_REFRESH_TARGETS.has(target)) {
      fetchOrderData({ silent: true });
      fetchActionData(orderId);
      fetchDocuments(orderId);
    }
  });

  useEffect(() => {
    documentsRequestIdRef.current += 1;
    setOrder(null);
    setDocuments([]);
    setStatusHistory([]);
    setSuggestedPrices([]);
    setUnreadMessagesCount(0);
    setActiveTab("actions");
    setIsOpenChat(false);
    fetchOrderData();

    return () => {
      requestIdRef.current += 1;
      documentsRequestIdRef.current += 1;
      actionsRequestIdRef.current += 1;
    };
  }, [fetchOrderData]);

  useEffect(() => {
    if (!order?.id) return undefined;

    let isActive = true;

    Aviation.getUnreadMessages(order.id)
      .then((response) => {
        if (isActive && response.status === 200) {
          setUnreadMessagesCount(response.data?.count || 0);
        }
      })
      .catch((error) => console.error(error));

    fetchActionData(order.id);
    fetchDocuments(order.id);

    return () => {
      isActive = false;
    };
  }, [fetchActionData, fetchDocuments, order?.id]);

  const isAccessRestricted =
    user?.type === "Expeditor" &&
    order &&
    order.status !== "awaitingoffer" &&
    order.companyId !== user.companyId &&
    order.expeditorId !== user.companyId;

  const handleOpenChat = () => {
    setIsOpenChat(true);
    scheduleUnreadRefresh();
  };

  const handleCloseChat = () => {
    setIsOpenChat(false);
    refreshUnreadOnClose();
  };

  return (
    <>
      <div
        className={cn(style.overlay, { [style.overlay_open]: isOpen })}
        onClick={() => setIsOpen(false)}
      />
      <div className={cn(style.wrapper, { [style.open]: isOpen })}>
        {order ? (
          <OrderInfoAviationHeader
            item={order}
            setIsOpen={setIsOpen}
            onOpenChat={handleOpenChat}
            unreadCount={unreadMessagesCount}
            onForceUpdate={onForceUpdate}
            hasBids={suggestedPrices.length > 0}
            onOrderDataChanged={() => {
              fetchOrderData();
              fetchActionData(order.id);
              onForceUpdate?.();
            }}
          />
        ) : null}

        {isLoading ? <Loading borderRadius="0" /> : null}

        {order && !isLoading ? (
          isAccessRestricted ? (
            <OrderAccessRestricted assignmentMethod={order.assignmentMethod} onClose={() => setIsOpen(false)} />
          ) : (
            <>
              <OrderInfoAviationStepper status={order.status} assignmentMethod={order.assignmentMethod} />
              <OrderInfoAviationTabs
                activeTab={activeTab}
                onTabChange={setActiveTab}
                documentsCount={documents.length}
              />
              <div className={style.content} data-testid="aviation-tab-content">
                {activeTab === "actions" ? (
                  <AviationActionsTab
                    order={order}
                    statusHistory={statusHistory}
                    suggestedPrices={suggestedPrices}
                    refreshOrderData={(options) => Promise.all([fetchOrderData(options), fetchActionData(order.id)])}
                    onForceUpdate={onForceUpdate}
                  />
                ) : null}
                {activeTab === "cargo" ? <AviationCargoTab order={order} /> : null}
                {activeTab === "documents" ? (
                  <AviationDocumentsTab
                    order={order}
                    documents={documents}
                    refreshDocuments={() => fetchDocuments(order.id)}
                  />
                ) : null}
              </div>
            </>
          )
        ) : null}
      </div>

      {isOpenChat && order ? (
        <OrdersChatHub
          isOpen={isOpenChat}
          orderData={order}
          orderId={order.id}
          orderNumber={order.number}
          onClose={handleCloseChat}
          serviceType="aviation"
        />
      ) : null}
    </>
  );
};

export default withTenderTime(OrderInfoAviation);
